/** Member-facing club points reference — fixed rules, not editable per round. */

export interface ClubPointGuideItem {
  label: string;
  /** Points earned (negative = penalty). Use null for variable / see note. */
  points: number | null;
  note?: string;
}

export interface ClubPointGuideSection {
  id: string;
  title: string;
  subtitle?: string;
  items: ClubPointGuideItem[];
}

export const CLUB_POINTS_GUIDE_SECTIONS: ClubPointGuideSection[] = [
  {
    id: 'participation',
    title: 'Participation',
    subtitle: 'Highest volume — post rounds to the timeline to count streaks.',
    items: [
      { label: 'Play a round', points: 100 },
      { label: 'Complete all 18 holes and post on timeline', points: 25 },
      { label: 'Complete 9 holes and post on timeline', points: 15 },
      { label: 'Play consecutive days and post on timeline', points: 25, note: 'Per day' },
      { label: 'Weekly streak bonus (3 rounds posted)', points: 100 },
      { label: 'Monthly streak (8 rounds posted)', points: 300 },
    ],
  },
  {
    id: 'community',
    title: 'Community Growth',
    items: [
      { label: 'Invite a member (joins)', points: 250 },
      { label: 'Invite a member (plays first round)', points: 500 },
      { label: 'Refer a club that activates', points: 2500 },
      { label: 'Complete profile', points: 50 },
      { label: 'Add profile photo', points: 25 },
      { label: 'First post', points: 50 },
      { label: 'Comment', points: 5 },
      { label: 'Like / react', points: 2 },
      { label: 'Upload round photos', points: 10 },
    ],
  },
  {
    id: 'competition',
    title: 'Competition',
    items: [
      { label: 'Win a match', points: 100 },
      { label: 'Beat predicted score', points: 50 },
      { label: 'Upset (beat lower handicap)', points: 75 },
    ],
  },
  {
    id: 'performance',
    title: 'Performance Bonuses',
    subtitle: 'Stack with participation. Game stake settings do not affect club points.',
    items: [
      { label: 'Par', points: 5 },
      { label: 'Birdie', points: 15 },
      { label: 'Eagle', points: 50 },
      { label: 'Albatross', points: 250 },
      { label: 'Hole in one', points: 5000 },
      { label: 'Bogey', points: -15 },
      { label: 'Double bogey', points: -25 },
      { label: 'Triple bogey (or worse)', points: -50 },
      { label: 'No three-putts (round)', points: 25 },
      { label: 'Under-par nine', points: 50 },
      { label: 'Personal best score', points: 200 },
      { label: 'Break 100', points: 75 },
      { label: 'Break 90', points: 100 },
      { label: 'Break 80', points: 150 },
      { label: 'Break 70', points: 300 },
      { label: 'Lowest score at course (personal)', points: 150 },
    ],
  },
  {
    id: 'milestones',
    title: 'Milestones',
    items: [
      { label: 'First round', points: 250 },
      { label: '5 rounds', points: 250 },
      { label: '10 rounds', points: 500 },
      { label: '25 rounds', points: 1000 },
      { label: '50 rounds', points: 2000 },
      { label: '100 rounds', points: 5000 },
      { label: 'One-year member anniversary', points: 500 },
      { label: 'Birthday round', points: 100 },
    ],
  },
  {
    id: 'events',
    title: 'Special Events',
    items: [
      { label: 'Tournament entry', points: 100 },
      { label: 'Tournament winner', points: 500 },
      { label: 'Club Championship winner', points: 1500 },
      { label: 'Member-Guest participation', points: 100 },
      { label: 'Ryder Cup participant', points: 150 },
      { label: 'Wolf win', points: 75 },
      { label: 'Skins win', points: 50 },
      { label: 'Stableford win', points: 75 },
      { label: 'Scramble win', points: 100 },
    ],
  },
  {
    id: 'call-your-shot',
    title: 'Call Your Shot',
    subtitle: 'Predict your score before you play.',
    items: [
      {
        label: 'Hit your called score exactly',
        points: 150,
      },
      {
        label: 'Miss your called score',
        points: null,
        note: '−10 points for every stroke over your call',
      },
      {
        label: 'Beat your called score',
        points: null,
        note: '+25 points for every stroke under your call',
      },
    ],
  },
];

/** Format points for display (+100, −15, or em dash for variable). */
export function formatGuidePoints(points: number | null): string {
  if (points == null) return '—';
  if (points > 0) return `+${points.toLocaleString()}`;
  return points.toLocaleString();
}

/** Core values used by the round processor today — keep in sync as awards roll out. */
export const CLUB_POINT_RULES = {
  PLAY_ROUND: 100,
  POST_18: 25,
  POST_9: 15,
  MATCH_WIN: 100,
  BIRDIE: 15,
  PAR: 5,
  EAGLE: 50,
  ALBATROSS: 250,
  HOLE_IN_ONE: 5000,
  BOGEY: -15,
  DOUBLE_BOGEY: -25,
  TRIPLE_BOGEY: -50,
  CALL_SHOT_HIT: 150,
  CALL_SHOT_MISS_PER_STROKE: -10,
  CALL_SHOT_BEAT_PER_STROKE: 25,
  INVITE_JOINED: 250,
  INVITE_FIRST_ROUND: 500,
} as const;

/** @deprecated use CLUB_POINT_RULES */
export const POINT_RULES = {
  POST_ROUND: CLUB_POINT_RULES.PLAY_ROUND,
  BEAT_HANDICAP: 0,
  WON_NASSAU: CLUB_POINT_RULES.MATCH_WIN,
  WON_SKIN: 50,
  CORRECT_PREDICTION: CLUB_POINT_RULES.CALL_SHOT_HIT,
  HOT_HAND_BONUS: 25,
  COURSE_RECORD: 150,
  EAGLE_HOLE: CLUB_POINT_RULES.EAGLE,
  HOLE_IN_ONE: CLUB_POINT_RULES.HOLE_IN_ONE,
  INVITE_JOINED: CLUB_POINT_RULES.INVITE_JOINED,
} as const;

export type ClubPointRuleKey = keyof typeof CLUB_POINT_RULES;
